import DOMPurify from "dompurify";
import type { CashflowEvent } from "@/features/enterprises/types";
import { formatDate } from "@/utils/formatDate";
import type {
  SimStaffResult,
  SimulationAttachment,
  SimulationComment,
  SimulationResult,
} from "@/features/finances/types/simulation";

// Strukturalny podzbiór draftu (przyjmuje pełny SimDraft ze strony).
interface PrintVariant {
  id: string;
  name: string;
  order_value: number;
  end_date: string;
  reqs: {
    desc: string;
    deadline: string | null;
    items: { name: string; qty: string; rate: string }[];
    payments: { date: string; pct: number }[];
  }[];
  revenue: { date: string; amount: number }[];
}
interface SimPrintDraft {
  name: string;
  source?: string;
  linked_label?: string;
  start_year: number;
  start_month: number;
  variants: PrintVariant[];
}

const MONTHS = [
  "sty",
  "lut",
  "mar",
  "kwi",
  "maj",
  "cze",
  "lip",
  "sie",
  "wrz",
  "paź",
  "lis",
  "gru",
];
const SOURCE_LABEL: Record<string, string> = {
  manual: "Z ręki",
  group: "Z grupy",
  tender: "Z przetargu",
  ze: "Z przetargu ZE",
};

const fmt = (v: string | number | null | undefined): string => {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  return isNaN(n)
    ? "—"
    : n.toLocaleString("pl-PL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: "always",
      });
};
const pln = (v: string | number | null | undefined): string => `${fmt(v)} PLN`;
const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fmtDate = (d: string | null | undefined): string => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return day ? `${day}.${m}.${y}` : `${m}.${y}`;
};
const fmtDateTime = (iso: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};
const tsLabel = (ts: number): string =>
  new Date(ts).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
const fmtK = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${Math.round(v / 1e3)}k`;
  return `${Math.round(v)}`;
};
const shortM = (y: number, m: number) =>
  `${MONTHS[m - 1]} ${String(y).slice(2)}`;
const ymOf = (iso: string) => {
  if (!iso) return null;
  const [y, m] = iso.split("-").map(Number);
  return y && m ? { year: y, month: m } : null;
};
const addM = (y: number, m: number, n: number) => {
  let yy = y,
    mm = m + n;
  while (mm > 12) {
    mm -= 12;
    yy++;
  }
  return { year: yy, month: mm };
};
function monthsOf(
  sy: number,
  sm: number,
  ey: number,
  em: number,
): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  let y = sy,
    m = sm;
  for (let i = 0; i < 60; i++) {
    out.push({ year: y, month: m });
    if (y === ey && m === em) break;
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

function sanitizeComment(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "ul",
      "ol",
      "li",
      "img",
      "h1",
      "h2",
      "h3",
    ],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
  // Okno wydruku (about:blank) nie ma originu — zamień względne /media/... na absolutne, by obrazki się załadowały.
  return clean.replace(/src="\/(?!\/)/g, `src="${window.location.origin}/`);
}

// ── Nałożenie cashflow GK + symulacji (lustro logiki SimGroupOverlayChart) ─────
const COLOR_GROUP = "#64748b";
const COLOR_SIM = "#f59e0b";
const COLOR_SUM = "#2563eb";
const DAY_MS = 86_400_000;
const round2 = (n: number) => Math.round(n * 100) / 100;
interface OvRow {
  ts: number;
  group: number;
  sim: number;
  sum: number;
}
interface OvMin {
  value: number;
  ts: number;
}

function cumulativeByDate(
  events: CashflowEvent[],
  start: number,
): { ts: number; bal: number }[] {
  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const out: { ts: number; bal: number }[] = [];
  let run = start,
    curDate: string | null = null;
  for (const ev of sorted) {
    run += parseFloat(ev.amount) || 0;
    if (ev.date === curDate && out.length)
      out[out.length - 1].bal = round2(run);
    else {
      curDate = ev.date;
      out.push({ ts: new Date(ev.date).getTime(), bal: round2(run) });
    }
  }
  return out;
}
function seriesMin(core: OvRow[], pick: (r: OvRow) => number): OvMin {
  let best = core[0],
    bestVal = pick(core[0]);
  for (const r of core) {
    const v = pick(r);
    if (v < bestVal) {
      bestVal = v;
      best = r;
    }
  }
  return { value: bestVal, ts: best.ts };
}
function buildOverlayRows(
  simEvents: CashflowEvent[],
  groupEvents: CashflowEvent[],
  groupInitial: number,
  todayStr: string,
): { rows: OvRow[]; groupMin: OvMin | null; sumMin: OvMin | null } {
  const todayTs = new Date(todayStr).getTime();
  const groupBefore = groupEvents
    .filter((ev) => ev.date < todayStr)
    .reduce((s, ev) => s + (parseFloat(ev.amount) || 0), 0);
  const groupStart = round2(groupInitial - groupBefore);
  const groupSteps = cumulativeByDate(groupEvents, groupStart);
  const simSteps = cumulativeByDate(simEvents, 0);
  const tsSet = new Set<number>();
  for (const p of groupSteps) tsSet.add(p.ts);
  for (const p of simSteps) tsSet.add(p.ts);
  tsSet.add(todayTs);
  const tsList = [...tsSet].sort((a, b) => a - b);
  let gi = 0,
    si = 0,
    gBal = groupStart,
    sBal = 0;
  const core: OvRow[] = tsList.map((ts) => {
    while (gi < groupSteps.length && groupSteps[gi].ts <= ts) {
      gBal = groupSteps[gi].bal;
      gi++;
    }
    while (si < simSteps.length && simSteps[si].ts <= ts) {
      sBal = simSteps[si].bal;
      si++;
    }
    return { ts, group: gBal, sim: sBal, sum: round2(gBal + sBal) };
  });
  // Okno = zakres dat zdarzeń symulacji (+ dziś), jak wykres bez nakładki — nie cały
  // horyzont GK ELEKTIM, który sięga dalej w przyszłość.
  const simTs = simEvents.map((ev) => new Date(ev.date).getTime());
  const winLo = simTs.length ? Math.min(todayTs, ...simTs) : core[0]?.ts ?? todayTs;
  const winHi = simTs.length
    ? Math.max(todayTs, ...simTs)
    : core[core.length - 1]?.ts ?? todayTs;
  const base = core.filter((r) => r.ts >= winLo && r.ts <= winHi);
  const groupMin = base.length ? seriesMin(base, (r) => r.group) : null;
  const sumMin = base.length ? seriesMin(base, (r) => r.sum) : null;
  const balBefore = (
    steps: { ts: number; bal: number }[],
    ts: number,
    start: number,
  ): number => {
    let b = start;
    for (const s of steps) {
      if (s.ts < ts) b = s.bal;
      else break;
    }
    return b;
  };
  const rows: OvRow[] = [...base];
  if (rows.length) {
    const firstTs = rows[0].ts,
      lastTs = rows[rows.length - 1].ts;
    const span = Math.max(lastTs - firstTs, DAY_MS);
    const pad = Math.max(span * 0.04, DAY_MS * 2);
    const enterGroup = balBefore(groupSteps, firstTs, groupStart);
    const enterSim = balBefore(simSteps, firstTs, 0);
    rows.unshift({
      ts: firstTs - pad,
      group: enterGroup,
      sim: enterSim,
      sum: round2(enterGroup + enterSim),
    });
    const last = rows[rows.length - 1];
    rows.push({
      ts: lastTs + pad,
      group: last.group,
      sim: last.sim,
      sum: last.sum,
    });
  }
  return { rows, groupMin, sumMin };
}

// prosty słupkowy wykres inline-SVG (do druku)
function svgBars(
  data: { label: string; value: number }[],
  opts: { color?: string; negColor?: string; height?: number } = {},
): string {
  const W = 540,
    H = opts.height ?? 90,
    padB = 16,
    padL = 4,
    padT = 6;
  const n = data.length || 1;
  const max = Math.max(1, ...data.map((d) => Math.abs(d.value)));
  const bw = (W - padL * 2) / n;
  const zeroY = opts.negColor ? (H - padB) / 2 + padT : H - padB;
  const bars = data
    .map((d, i) => {
      const x = padL + i * bw + bw * 0.15;
      const w = bw * 0.7;
      const h =
        (Math.abs(d.value) / max) *
        ((opts.negColor ? (H - padB) / 2 : H - padB) - padT);
      const y = d.value >= 0 ? zeroY - h : zeroY;
      const fill =
        d.value < 0 && opts.negColor
          ? opts.negColor
          : (opts.color ?? "#2563eb");
      const lbl = `<text x="${x + w / 2}" y="${H - 4}" font-size="7" fill="#9ca3af" text-anchor="middle">${esc(d.label)}</text>`;
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${Math.max(0, h).toFixed(1)}" fill="${fill}" rx="1.5"/>${lbl}`;
    })
    .join("");
  const axis = `<line x1="${padL}" y1="${zeroY}" x2="${W - padL}" y2="${zeroY}" stroke="#e5e7eb" stroke-width="1"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none">${axis}${bars}</svg>`;
}

// paleta osób (lustro STAFF_COLORS z OrderSimulationPage)
const STAFF_COLORS_PDF = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#db2777",
];

// Skumulowany słupkowy „Obłożenie osób (% etatu)" — każda osoba kolorem, stos per
// miesiąc + linia 100% (lustro StaffSimChart z prawego panelu strony).
function svgStaffStack(
  staff: SimStaffResult[],
  months: { year: number; month: number }[],
): string {
  if (!staff.length || !months.length) return "";
  const W = 540,
    H = 150,
    padL = 4,
    padR = 4,
    padT = 8,
    padB = 16;
  const pctOf = (s: SimStaffResult, mm: { year: number; month: number }) =>
    Math.round(
      (s.by_month.find((x) => x.year === mm.year && x.month === mm.month)
        ?.fraction || 0) * 100,
    );
  const totals = months.map((mm) =>
    staff.reduce((acc, s) => acc + pctOf(s, mm), 0),
  );
  const maxY = Math.max(100, ...totals);
  const n = months.length;
  const bw = (W - padL - padR) / n;
  const plotH = H - padT - padB;
  const zeroY = padT + plotH;
  const yOf = (v: number) => zeroY - (v / maxY) * plotH;
  const y100 = yOf(100);
  const ref = `<line x1="${padL}" y1="${y100.toFixed(1)}" x2="${W - padR}" y2="${y100.toFixed(1)}" stroke="#9ca3af" stroke-width="1" stroke-dasharray="4 3"/><text x="${(W - padR).toFixed(1)}" y="${(y100 - 2).toFixed(1)}" font-size="7" fill="#9ca3af" text-anchor="end">100%</text>`;
  let bars = "";
  months.forEach((mm, i) => {
    const x = padL + i * bw + bw * 0.15;
    const w = bw * 0.7;
    let acc = 0;
    staff.forEach((s, si) => {
      const pct = pctOf(s, mm);
      if (pct <= 0) return;
      const h = (pct / maxY) * plotH;
      const yTop = yOf(acc + pct);
      const fill = STAFF_COLORS_PDF[si % STAFF_COLORS_PDF.length];
      bars += `<rect x="${x.toFixed(1)}" y="${yTop.toFixed(1)}" width="${w.toFixed(1)}" height="${Math.max(0, h).toFixed(1)}" fill="${fill}"/>`;
      if (h >= 9)
        bars += `<text x="${(x + w / 2).toFixed(1)}" y="${(yTop + h / 2).toFixed(1)}" font-size="6.5" fill="#fff" text-anchor="middle" dominant-baseline="central">${pct}%</text>`;
      acc += pct;
    });
    bars += `<text x="${(x + w / 2).toFixed(1)}" y="${H - 4}" font-size="7" fill="#9ca3af" text-anchor="middle">${esc(shortM(mm.year, mm.month))}</text>`;
  });
  const axis = `<line x1="${padL}" y1="${zeroY}" x2="${W - padR}" y2="${zeroY}" stroke="#e5e7eb" stroke-width="1"/>`;
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none">${axis}${ref}${bars}</svg>`;
}

// generyczny wykres liniowy salda (1..n serii) — do druku
function svgBalanceLines(
  rows: OvRow[],
  todayTs: number,
  series: { get: (r: OvRow) => number; color: string; width: number }[],
): string {
  if (!rows.length) return "";
  const W = 540,
    H = 170,
    padL = 4,
    padR = 4,
    padT = 10,
    padB = 16;
  const xs = rows.map((r) => r.ts);
  const xMin = Math.min(...xs),
    xMax = Math.max(...xs);
  const vals: number[] = [0];
  for (const r of rows) for (const s of series) vals.push(s.get(r));
  let yMin = Math.min(...vals),
    yMax = Math.max(...vals);
  if (yMin === yMax) yMax = yMin + 1;
  const plotW = W - padL - padR,
    plotH = H - padT - padB;
  const X = (ts: number) =>
    padL + (xMax > xMin ? (ts - xMin) / (xMax - xMin) : 0.5) * plotW;
  const Y = (v: number) => padT + ((yMax - v) / (yMax - yMin)) * plotH;
  const lines = series
    .map(
      (s) =>
        `<polyline fill="none" stroke="${s.color}" stroke-width="${s.width}" points="${rows.map((r) => `${X(r.ts).toFixed(1)},${Y(s.get(r)).toFixed(1)}`).join(" ")}"/>`,
    )
    .join("");
  const zeroY = Y(0);
  const zero = `<line x1="${padL}" y1="${zeroY.toFixed(1)}" x2="${W - padR}" y2="${zeroY.toFixed(1)}" stroke="#d1d5db" stroke-width="1"/>`;
  const today =
    todayTs >= xMin && todayTs <= xMax
      ? `<line x1="${X(todayTs).toFixed(1)}" y1="${padT}" x2="${X(todayTs).toFixed(1)}" y2="${H - padB}" stroke="#3b82f6" stroke-width="1" stroke-dasharray="4 3"/><text x="${(X(todayTs) + 2).toFixed(1)}" y="${padT + 7}" font-size="7" fill="#3b82f6">dziś</text>`
      : "";
  const yl = (v: number) =>
    `<text x="${padL + 2}" y="${(Y(v) - 2).toFixed(1)}" font-size="7" fill="#9ca3af">${fmtK(v)}</text>`;
  const xl = `<text x="${padL}" y="${H - 4}" font-size="7" fill="#9ca3af">${tsLabel(xMin)}</text><text x="${W - padR}" y="${H - 4}" font-size="7" fill="#9ca3af" text-anchor="end">${tsLabel(xMax)}</text>`;
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none">${zero}${today}${lines}${yl(yMax)}${yl(0)}${yl(yMin)}${xl}</svg>`;
}

// wykres liniowy salda Z nałożeniem GK (GK / symulacja / suma) — do druku
function svgOverlay(rows: OvRow[], todayTs: number): string {
  return svgBalanceLines(rows, todayTs, [
    { get: (r) => r.group, color: COLOR_GROUP, width: 1.5 },
    { get: (r) => r.sim, color: COLOR_SIM, width: 1.5 },
    { get: (r) => r.sum, color: COLOR_SUM, width: 2.25 },
  ]);
}

// Pełne rozbicie narzutu kosztów stałych — wg OBCIĄŻENIA KOSZTOWEGO (treść modala „skąd ta kwota").
function overheadHTML(r: SimulationResult): string {
  const b = r.overhead_basis;
  const rows = b.rows ?? [];
  const fixedPart = parseFloat(b.fixed_part) || 0;
  const peoplePart = parseFloat(b.people_part) || 0;
  const total = fixedPart + peoplePart;
  const rate = parseFloat(b.rate_effective) || 0;
  const monthRows = rows
    .map(
      (row) =>
        `<tr><td>${MONTHS[row.month - 1]} ${row.year}</td><td class="num">${pln(row.sim_load.toFixed(2))}</td><td class="num">${pln(row.existing_load.toFixed(2))}</td><td class="num">${(parseFloat(row.share) * 100).toFixed(1)}%</td><td class="num">${pln(row.pool)}</td><td class="num">${pln(row.narzut)}</td></tr>`,
    )
    .join("");
  return `<h3>Narzut kosztów stałych — wg obciążenia kosztowego (jak realne projekty)</h3>
    <div class="oh-explain">
      <p><b>Po co to.</b> Marża jest wiarygodna dopiero, gdy zlecenie pokrywa nie tylko koszty bezpośrednie (płace projektowe, podwykonawcy), ale też swój udział w kosztach utrzymania firmy (czynsz, księgowość, kadry, zarząd), których żadne pojedyncze zlecenie nie generuje wprost.</p>
      <p><b>Jak liczymy.</b> Tak samo jak na realnych projektach — wg OBCIĄŻENIA KOSZTOWEGO. W każdym miesiącu udział zlecenia = jego obciążenie ÷ (obciążenie istniejącego portfela GK + obciążenie zlecenia); udział × pula (koszty stałe + nieprojektowi) = narzut tego miesiąca. Suma po miesiącach = narzut całkowity.</p>
      <p style="font-size:9px;color:#6b7280"><b>Obciążenie kosztowe</b> = Σ (% etatu × miesięczna pensja danej osoby), w zł — nie suchy etat. Osoba na 50% za 12&nbsp;000 zł waży 6&nbsp;000 zł; droższa obsada obciąża firmę bardziej. Narzut rozkłada się wg realnego ciężaru pensji, nie liczby głów.</p>
    </div>
    <table class="oh"><thead><tr><th>Miesiąc</th><th class="num">Obciążenie zlecenia</th><th class="num">Portfel GK</th><th class="num">Udział</th><th class="num">Pula/mc</th><th class="num">Narzut</th></tr></thead><tbody>
      ${monthRows}
      <tr class="tot"><td>Razem (${rows.length} mc)</td><td></td><td></td><td class="num">${(rate * 100).toFixed(1)}% śr.</td><td class="num">${pln(b.total_pool)}</td><td class="num">${pln(total.toFixed(2))}</td></tr>
    </tbody></table>
    <table class="oh"><tbody>
      <tr><td>Koszty stałe (proporcjonalne)</td><td class="num">${pln(fixedPart.toFixed(2))}</td></tr>
      <tr><td>Nieprojektowi (proporcjonalni)</td><td class="num">${pln(peoplePart.toFixed(2))}</td></tr>
      <tr class="tot"><td>Narzut razem</td><td class="num">${pln(total.toFixed(2))}</td></tr>
    </tbody></table>
    <p class="oh-foot">Obciążenie kosztowe = Σ (% etatu × pensja) z prognozy/faktu (te same dane co wykresy obłożenia), nie suchy etat. Miesiąc bez obsady zlecenia → 0 narzutu. Im więcej (i droższej) równoległej pracy w GK, tym mniejszy udział tego zlecenia.</p>`;
}

function capacityWarningsHTML(result: SimulationResult | null): string {
  const ws = result?.capacity_warnings ?? [];
  if (!ws.length) return "";
  const rows = ws
    .map(
      (w) =>
        `<li><b>${esc(w.employee_name)}</b> — ${shortM(w.year, w.month)}: wliczone ${(w.requested * 100).toFixed(0)}%, wolne ${(w.available * 100).toFixed(0)}% (deficyt ${(w.deficit * 100).toFixed(0)}%). <span class="warn-opt">→ ${esc(w.options.join(" lub "))}</span></li>`,
    )
    .join("");
  return `<h3 class="warn-h">Ostrzeżenia o wolnych mocach (${ws.length})</h3><ul class="warns">${rows}</ul>`;
}

function attachmentsHTML(attachments: SimulationAttachment[]): string {
  if (!attachments.length) return "";
  const rows = attachments
    .map(
      (a) =>
        `<tr><td>${esc(a.original_name)}</td><td>${esc(a.uploaded_by_name || "—")}</td><td>${fmtDateTime(a.created_at)}</td></tr>`,
    )
    .join("");
  return `<section><h2>Załączniki (${attachments.length})</h2><p class="sub">Wspólne dla całej symulacji — niezależne od wariantów.</p><table><thead><tr><th>Nazwa pliku</th><th>Dodał</th><th>Data</th></tr></thead><tbody>${rows}</tbody></table></section>`;
}

function discussionHTML(comments: SimulationComment[]): string {
  if (!comments.length) return "";
  const items = comments
    .map((c) => {
      const body = (c.content || "").trimStart().startsWith("<")
        ? sanitizeComment(c.content)
        : `<p>${esc(c.content)}</p>`;
      const ment = c.mentions?.length
        ? `<div class="cmt-meta">Oznaczeni: ${c.mentions.map((m) => esc(m.full_name)).join(", ")}</div>`
        : "";
      const atts = c.attachments?.length
        ? `<div class="cmt-meta">Załączniki: ${c.attachments.map((a) => esc(a.original_name)).join(", ")}</div>`
        : "";
      return `<div class="cmt"><div class="cmt-h"><b>${esc(c.author_name)}</b><span class="cmt-d">${fmtDateTime(c.created_at)}</span></div><div class="cmt-b">${body}</div>${ment}${atts}</div>`;
    })
    .join("");
  return `<section><h2>Dyskusja (${comments.length})</h2><p class="sub">Wspólna dla całej symulacji — niezależna od wariantów.</p>${items}</section>`;
}

export function printSimulationPDF(
  draft: SimPrintDraft,
  results: Record<string, SimulationResult | null>,
  employees: { employee_id: string; employee_name: string }[],
  opts?: {
    comments?: SimulationComment[];
    attachments?: SimulationAttachment[];
    groupEvents?: CashflowEvent[];
    groupInitial?: number;
  },
) {
  const comments = opts?.comments ?? [];
  const attachments = opts?.attachments ?? [];
  const groupEvents = opts?.groupEvents ?? [];
  const groupInitial = opts?.groupInitial ?? 0;
  const nameOf = new Map(
    employees.map((e) => [e.employee_id, e.employee_name]),
  );

  // Tabela porównawcza wariantów (gdy jest ich więcej niż jeden).
  const summaryHTML = (): string => {
    if (draft.variants.length < 2) return "";
    const heads = draft.variants
      .map((v) => `<th class="num">${esc(v.name)}</th>`)
      .join("");
    const get = (v: PrintVariant) => results[v.id] ?? null;
    const row = (
      label: string,
      fn: (
        r: SimulationResult | null,
        v: PrintVariant,
      ) => { txt: string; cls?: string },
    ) =>
      `<tr><td>${label}</td>${draft.variants
        .map((v) => {
          const c = fn(get(v), v);
          return `<td class="num ${c.cls ?? ""}">${c.txt}</td>`;
        })
        .join("")}</tr>`;
    const mcls = (r: SimulationResult | null) =>
      r && parseFloat(r.margin) < 0 ? "neg" : "pos";
    const body = [
      row("Wartość zlecenia", (_r, v) => ({ txt: pln(v.order_value) })),
      row("Przychód", (r) => ({ txt: r ? pln(r.revenue_total) : "—" })),
      row("Koszt prognozowany", (r) => ({
        txt: r ? pln(r.cost_forecast) : "—",
      })),
      row("Marża", (r) => ({ txt: r ? pln(r.margin) : "—", cls: mcls(r) })),
      row("Marża %", (r) => ({
        txt: r && r.margin_pct != null ? `${r.margin_pct}%` : "—",
        cls: mcls(r),
      })),
    ].join("");
    return `<h3>Porównanie wariantów</h3><table class="cmp"><thead><tr><th>Pozycja</th>${heads}</tr></thead><tbody>${body}</tbody></table>`;
  };

  const variantHTML = (v: PrintVariant): string => {
    const r = results[v.id] ?? null;
    const endYM =
      ymOf(v.end_date) || addM(draft.start_year, draft.start_month, 11);
    const planEnd = addM(endYM.year, endYM.month, 1);
    const months = monthsOf(
      draft.start_year,
      draft.start_month,
      planEnd.year,
      planEnd.month,
    );
    const staff = r?.staff ?? [];
    const marginNeg = r ? parseFloat(r.margin) < 0 : false;

    // KPI + rozbicie kosztu
    const kpis = r
      ? `<div class="kpis">
      <div class="kpi"><div class="kpi-l">Przychód</div><div class="kpi-v">${pln(r.revenue_total)}</div></div>
      <div class="kpi"><div class="kpi-l">Koszt prognozowany</div><div class="kpi-v">${pln(r.cost_forecast)}</div></div>
      <div class="kpi ${marginNeg ? "neg" : "pos"}"><div class="kpi-l">Marża</div><div class="kpi-v">${pln(r.margin)}</div></div>
      <div class="kpi ${marginNeg ? "neg" : "pos"}"><div class="kpi-l">Marża %</div><div class="kpi-v">${r.margin_pct ?? "—"}${r.margin_pct != null ? "%" : ""}</div></div>
    </div>
    <table class="mini"><tbody>
      <tr><td>Płace (projektowi)</td><td class="num">${pln(r.salary_cost)}</td></tr>
      <tr><td>Narzut (obłożeniowy) — śr. ${(parseFloat(r.overhead_rate) * 100).toFixed(1)}% udziału przez ${r.overhead_basis.months} mc</td><td class="num">${pln(r.overhead_cost)}</td></tr>
      <tr><td>Podwykonawcy i koszty jednorazowe</td><td class="num">${pln(r.contractor_cost)}</td></tr>
      <tr class="tot"><td>Koszt razem</td><td class="num">${pln(r.cost_forecast)}</td></tr>
    </tbody></table>
    ${overheadHTML(r)}`
      : `<p class="muted">Brak policzonego wyniku — kliknij „przelicz wszystkie".</p>`;

    // Obłożenie: tabela + skumulowany wykres osób (jak w prawym panelu strony)
    const staffName = (s: SimStaffResult) =>
      s.is_new
        ? `${s.label || "Nowy etat"}${s.rate_pattern_employee_id ? ` (jak ${nameOf.get(s.rate_pattern_employee_id) || "—"})` : ""}`
        : nameOf.get(s.employee_id ?? "") || s.label || "Pracownik";
    const occHead = `<th>Osoba</th>${months.map((mm) => `<th class="num">${shortM(mm.year, mm.month)}</th>`).join("")}`;
    const occRows = staff
      .map((s) => {
        const cells = months
          .map((mm) => {
            const c = s.by_month.find(
              (x) => x.year === mm.year && x.month === mm.month,
            );
            const p = c ? Math.round(c.fraction * 100) : 0;
            return `<td class="num">${p ? p + "%" : "·"}</td>`;
          })
          .join("");
        return `<tr><td>${esc(staffName(s))}</td>${cells}</tr>`;
      })
      .join("");
    const occLegend = `<div class="legend">${staff
      .map(
        (s, i) =>
          `<span><i style="background:${STAFF_COLORS_PDF[i % STAFF_COLORS_PDF.length]}"></i>${esc(staffName(s))}</span>`,
      )
      .join("")}</div>`;
    const occBlock = staff.length
      ? `<table class="grid"><thead><tr>${occHead}</tr></thead><tbody>${occRows}</tbody></table>
         <div class="chart"><div class="chart-t">Obłożenie osób (% etatu)</div>${occLegend}${svgStaffStack(staff, months)}</div>`
      : `<p class="muted">Brak przydziałów ludzi.</p>`;

    // Podwykonawcy — każdy z subtabelą przelewów (data · % · kwota netto)
    const reqBlock = v.reqs.length
      ? v.reqs
          .map((rq) => {
            const total = rq.items.reduce(
              (s, it) =>
                s + (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0),
              0,
            );
            const explicit = rq.payments.filter((p) => p.date && p.pct > 0);
            const rem = 100 - explicit.reduce((s, p) => s + p.pct, 0);
            const pays = explicit.map((p) => ({
              when: fmtDate(p.date),
              pct: p.pct,
              amt: (total * p.pct) / 100,
            }));
            if (rem > 0.01 && rq.deadline)
              pays.push({
                when: `${fmtDate(rq.deadline)} (domknięcie)`,
                pct: Math.round(rem * 10) / 10,
                amt: (total * rem) / 100,
              });
            const itemsLine = rq.items
              .filter((it) => it.name)
              .map((it) => `${esc(it.name)} (${fmt(it.qty)}×${fmt(it.rate)})`)
              .join(", ");
            const payRows = pays.length
              ? pays
                  .map(
                    (p) =>
                      `<tr><td>${p.when}</td><td class="num">${p.pct}%</td><td class="num">${fmt(p.amt)}</td></tr>`,
                  )
                  .join("")
              : `<tr><td colspan="3" class="muted">Brak punktów płatności (cała kwota na terminie).</td></tr>`;
            return `<div class="reqcard">
            <div class="reqhead"><b>${esc(rq.desc || "Podwykonawca")}</b><span>termin ${fmtDate(rq.deadline)} · wartość ${pln(total)} netto</span></div>
            ${itemsLine ? `<div class="reqitems">${itemsLine}</div>` : ""}
            <table class="sub"><thead><tr><th>Przelew (data)</th><th class="num">% wartości</th><th class="num">Kwota netto</th></tr></thead><tbody>${payRows}</tbody></table>
          </div>`;
          })
          .join("")
      : `<p class="muted">Brak podwykonawców.</p>`;

    // Wpływy (netto) — z udziałem % w wartości zlecenia
    const partial = v.revenue.reduce(
      (s, t) => s + (t.amount > 0 ? t.amount : 0),
      0,
    );
    const finalRem = Math.max(0, v.order_value - partial);
    const revTot = partial + finalRem;
    const revDenom = v.order_value > 0 ? v.order_value : revTot;
    const revPct = (a: number) =>
      revDenom > 0 ? `${((a / revDenom) * 100).toFixed(1)}%` : "—";
    const revRows = v.revenue
      .filter((t) => t.date && t.amount > 0)
      .map(
        (t) =>
          `<tr><td>${fmtDate(t.date)}</td><td class="num">${fmt(t.amount)}</td><td class="num">${revPct(t.amount)}</td></tr>`,
      )
      .join("");
    const revFinal =
      finalRem > 0
        ? `<tr><td>${shortM(endYM.year, endYM.month)} (domknięcie)</td><td class="num">${fmt(finalRem)}</td><td class="num">${revPct(finalRem)}</td></tr>`
        : "";
    const revTotRow =
      revRows || revFinal
        ? `<tr class="tot"><td>Razem (netto)</td><td class="num">${fmt(revTot)}</td><td class="num">${revDenom > 0 ? `${Math.round((revTot / revDenom) * 100)}%` : "—"}</td></tr>`
        : "";
    const revBlock = `<table><thead><tr><th>Data wpływu</th><th class="num">Kwota netto</th><th class="num">% wartości</th></tr></thead><tbody>${revRows}${revFinal}${revTotRow || `<tr><td colspan="3" class="muted">Brak wpływów.</td></tr>`}</tbody></table>`;

    // Cashflow: netto per miesiąc
    const net: Record<string, number> = {};
    for (const ev of r?.cashflow_events ?? []) {
      const k = (ev.date || "").slice(0, 7);
      net[k] = (net[k] || 0) + (parseFloat(ev.amount) || 0);
    }
    const cfData = months.map((mm) => ({
      label: shortM(mm.year, mm.month),
      value: Math.round(
        net[`${mm.year}-${String(mm.month).padStart(2, "0")}`] || 0,
      ),
    }));
    const cfBlock = `<div class="chart"><div class="chart-t">Przepływy netto / miesiąc</div>${svgBars(cfData, { color: "#16a34a", negColor: "#dc2626" })}</div>`;

    const simEvents = r?.cashflow_events ?? [];
    const today = new Date().toISOString().split("T")[0];
    const todayTs = new Date(today).getTime();

    // Prognozowane przepływy — skumulowane saldo SAMEGO zlecenia (bez GK ELEKTIM).
    // Lustro CashflowChart z ekranu przy WYŁĄCZONYM przełączniku „Nałóż pełny przepływ GK".
    // Pusty zestaw zdarzeń GK ⇒ group=0, więc seria „sim" = czyste saldo zlecenia (to samo okno co overlay).
    let simBalanceBlock = "";
    if (simEvents.length) {
      const { rows } = buildOverlayRows(simEvents, [], 0, today);
      if (rows.length)
        simBalanceBlock = `<div class="chart"><div class="chart-t">Skumulowane saldo — samo zlecenie (bez GK ELEKTIM)</div>${svgBalanceLines(
          rows,
          todayTs,
          [{ get: (row) => row.sim, color: COLOR_SUM, width: 2.25 }],
        )}</div>`;
    }

    // Nałożenie na płynność GK ELEKTIM (per wariant) — wersja Z nałożeniem.
    let overlayBlock = "";
    if (groupEvents.length && simEvents.length) {
      const { rows, groupMin, sumMin } = buildOverlayRows(
        simEvents,
        groupEvents,
        groupInitial,
        today,
      );
      if (rows.length) {
        const legend = `<div class="legend">${[
          [COLOR_GROUP, "GK ELEKTIM (bez zlecenia)"],
          [COLOR_SIM, "Symulacja (samo zlecenie)"],
          [COLOR_SUM, "Suma (GK + zlecenie)"],
        ]
          .map(([c, l]) => `<span><i style="background:${c}"></i>${l}</span>`)
          .join("")}</div>`;
        let cards = "";
        if (groupMin && sumMin) {
          const delta = round2(sumMin.value - groupMin.value);
          const deltaLine =
            delta === 0
              ? ""
              : `<div class="mc-delta ${delta < 0 ? "neg" : "pos"}">${delta < 0 ? "" : "+"}${fmt(delta)} PLN vs bez zlecenia</div>`;
          cards = `<div class="mincards">
            <div class="mincard"><div class="mc-l">Min. globalne GK — bez zlecenia</div><div class="mc-v ${groupMin.value < 0 ? "neg" : ""}">${pln(groupMin.value)}</div><div class="mc-d">najtrudniejszy dzień: ${formatDate(groupMin.ts)}</div></div>
            <div class="mincard hl"><div class="mc-l">Min. globalne GK — ze zleceniem</div><div class="mc-v ${sumMin.value < 0 ? "neg" : ""}">${pln(sumMin.value)}</div><div class="mc-d">najtrudniejszy dzień: ${formatDate(sumMin.ts)}</div>${deltaLine}</div>
          </div>`;
        }
        overlayBlock = `<h3>Wpływ na płynność GK ELEKTIM</h3>${legend}<div class="chart">${svgOverlay(rows, todayTs)}</div>${cards}<p class="muted">GK zakotwiczony do realnego salda kont na dziś. „Suma" = wpływ nowego zlecenia na łączny przepływ grupy. Minimum globalne = najtrudniejszy moment płynnościowy.</p>`;
      }
    }

    // Każdy wariant od nowej strony (gdy jest ich więcej niż jeden); nagłówek + porównanie zostają na 1. stronie.
    return `<section class="variant ${draft.variants.length > 1 ? "pagebreak" : ""}">
      <h2>${esc(v.name)}</h2>
      <p class="sub">Wartość ${pln(v.order_value)} · termin ${fmtDate(v.end_date)} · plan ${shortM(draft.start_year, draft.start_month)} – ${shortM(endYM.year, endYM.month)}</p>
      ${kpis}
      <h3>Obłożenie ludzi</h3>${occBlock}
      ${capacityWarningsHTML(r)}
      <h3>Podwykonawcy i koszty jednorazowe</h3>${reqBlock}
      <h3>Wpływy</h3>${revBlock}
      <h3>Cashflow zlecenia</h3>${simBalanceBlock}${cfBlock}
      ${overlayBlock}
    </section>`;
  };

  const provenance =
    draft.source && draft.source !== "manual"
      ? `<span class="badge">${esc(SOURCE_LABEL[draft.source] ?? draft.source)}</span>${draft.linked_label ? esc(draft.linked_label) + " · " : ""}`
      : "";
  const tailInner = [attachmentsHTML(attachments), discussionHTML(comments)]
    .filter(Boolean)
    .join("");

  const html = `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>${esc(draft.name)} — symulacja</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    * { box-sizing: border-box; } body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111827; font-size: 11px; }
    .head { border-bottom: 3px solid #2563eb; padding-bottom: 8px; margin-bottom: 12px; }
    .head h1 { font-size: 20px; margin: 0; color: #111827; }
    .head .meta { font-size: 9px; color: #6b7280; margin-top: 3px; }
    .badge { display: inline-block; background: #eef2ff; color: #3730a3; border-radius: 10px; padding: 1px 7px; font-size: 8px; font-weight: 700; margin-right: 6px; text-transform: uppercase; letter-spacing: .04em; }
    h2 { font-size: 15px; margin: 0 0 2px; color: #1e3a8a; }
    h3 { font-size: 10px; text-transform: uppercase; letter-spacing: .07em; color: #374151; margin: 14px 0 4px; padding-left: 8px; border-left: 3px solid #2563eb; }
    h3.warn-h { color: #92400e; border-color: #f59e0b; }
    .sub { color: #6b7280; margin: 0 0 8px; }
    .muted { color: #9ca3af; font-size: 10px; }
    .kpis { display: flex; gap: 8px; margin: 8px 0; }
    .kpi { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 6px 9px; }
    .kpi.pos { border-color: #bbf7d0; background: #f0fdf4; } .kpi.pos .kpi-v { color: #16a34a; }
    .kpi.neg { border-color: #fecaca; background: #fef2f2; } .kpi.neg .kpi-v { color: #dc2626; }
    .kpi-l { font-size: 9px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; }
    .kpi-v { font-size: 14px; font-weight: 700; }
    table { width: 100%; border-collapse: collapse; margin-top: 3px; }
    th, td { text-align: left; padding: 3px 6px; border-bottom: 1px solid #eef0f2; }
    th { background: #f9fafb; font-size: 9px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
    tbody tr:nth-child(even) td { background: #fcfcfd; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    table.grid th, table.grid td { padding: 2px 4px; font-size: 8.5px; }
    table.mini td { font-size: 10px; } table.mini tr.tot td { font-weight: 700; border-top: 1px solid #d1d5db; }
    table.oh { margin-bottom: 6px; } table.oh td, table.oh th { font-size: 9.5px; padding: 2px 6px; } table.oh tr.tot td { font-weight: 700; border-top: 1px solid #d1d5db; }
    .oh-explain { font-size: 9.5px; color: #374151; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 9px; margin: 4px 0 8px; line-height: 1.4; }
    .oh-explain p { margin: 0 0 4px; } .oh-explain p:last-child { margin-bottom: 0; }
    .oh-foot { font-size: 9px; color: #6b7280; font-style: italic; margin: 4px 0 0; line-height: 1.4; }
    .oh-warn { font-size: 9.5px; color: #92400e; background: #fffbeb; border: 1px solid #fcd34d; border-left: 3px solid #f59e0b; border-radius: 6px; padding: 6px 9px; margin: 6px 0; line-height: 1.4; }
    .oh-warn b { color: #b45309; }
    table.cmp td:first-child, table.cmp th:first-child { color: #6b7280; text-align: left; }
    table.cmp td.pos { color: #16a34a; font-weight: 600; } table.cmp td.neg { color: #dc2626; font-weight: 600; }
    .chart { margin-top: 6px; } .chart-t { font-size: 9px; color: #6b7280; margin-bottom: 2px; }
    .legend { display: flex; flex-wrap: wrap; gap: 12px; margin: 2px 0 4px; font-size: 8.5px; color: #6b7280; }
    .legend span { display: inline-flex; align-items: center; } .legend i { display: inline-block; width: 9px; height: 9px; border-radius: 2px; margin-right: 4px; }
    .mincards { display: flex; gap: 10px; margin-top: 8px; }
    .mincard { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 9px 11px; }
    .mincard.hl { border-color: #c7d2fe; background: #eef2ff; }
    .mc-l { font-size: 9px; text-transform: uppercase; letter-spacing: .03em; color: #4b5563; font-weight: 600; }
    .mc-v { font-size: 16px; font-weight: 700; margin-top: 2px; } .mc-v.neg { color: #dc2626; }
    .mc-d { font-size: 9px; color: #6b7280; margin-top: 2px; }
    .mc-delta { font-size: 9.5px; font-weight: 700; margin-top: 3px; }
    .reqcard { border: 1px solid #e5e7eb; border-radius: 8px; padding: 7px 9px; margin-top: 6px; page-break-inside: avoid; }
    .reqhead { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 10.5px; }
    .reqhead span { color: #6b7280; font-size: 9px; white-space: nowrap; }
    .reqitems { font-size: 8.5px; color: #6b7280; margin: 1px 0 2px; }
    table.sub { margin-top: 2px; } table.sub th, table.sub td { font-size: 9px; padding: 2px 5px; }
    tbody tr.tot td { font-weight: 700; border-top: 1px solid #d1d5db; }
    .pos { color: #16a34a; } .neg { color: #dc2626; }
    .variant { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; margin-bottom: 12px; }
    .variant.pagebreak { page-break-before: always; }
    .pagebreak { page-break-before: always; }
    .warns { margin: 4px 0 0; padding-left: 16px; }
    .warns li { color: #92400e; font-size: 10px; margin-bottom: 3px; }
    .warn-opt { color: #b45309; }
    .cmt { border-left: 3px solid #2563eb; background: #f9fafb; border-radius: 0 6px 6px 0; padding: 5px 8px; margin-bottom: 6px; page-break-inside: avoid; }
    .cmt-h { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
    .cmt-d { color: #9ca3af; font-size: 9px; white-space: nowrap; }
    .cmt-b { font-size: 10px; margin-top: 2px; }
    .cmt-b p { margin: 2px 0; }
    .cmt-b img { max-width: 100%; max-height: 220px; }
    .cmt-meta { font-size: 9px; color: #6b7280; margin-top: 2px; }
    .foot { margin-top: 16px; color: #9ca3af; font-size: 9px; }
  </style></head><body>
    <div class="head">
      <h1>${esc(draft.name)}</h1>
      <div class="meta">${provenance}Symulacja zlecenia / wyceny · ${draft.variants.length} ${draft.variants.length === 1 ? "wariant" : "wariantów"} · start ${shortM(draft.start_year, draft.start_month)} · wygenerowano ${new Date().toLocaleString("pl-PL")}</div>
    </div>
    ${summaryHTML()}
    ${tailInner}
    ${draft.variants.map((v) => variantHTML(v)).join("")}
    <p class="foot">Wszystkie kwoty netto. Dokument poglądowy (symulacja).</p>
    <script>window.onload=function(){setTimeout(function(){window.print();},600);};</script>
  </body></html>`;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
